import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResource,
    IWorkspaceResourceFolder,
    isAllTeamMembersFolder,
    PermissionActionType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspacePlanTier,
} from "common/interfaces";
import {
    PresentationLibraryFilter,
    PresentationLibraryFilterType,
} from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, {
    getWorkspaceResources,
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowMessageDialog,
} from "js/react/components/Dialogs/BaseDialog";
import { DeleteWorkspaceResourceFolderDialog } from "js/react/components/Dialogs/DeleteWorkspaceResourceFolderDialog";
import { IconMenu } from "js/react/components/UiComponents";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/ManageTeamFolderMembersDialog";
import { t } from "js/i18n";

const logger = getLogger();

const HeaderActionIconMenu = styled(IconMenu).attrs({
    size: "small",
    fontSize: "small",
})`
    &&& {
        padding: 0px;
    }
`;

const StyledIcon = styled(Icon).attrs({ className: "material-symbols-outlined" })`
    &&& {
        margin-right: 0px;
        color: #666;
        font-size: 20px;
    }
`;

const StyledSpan = styled.span`
    color: #222;
    /* popup/category header */
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%; /* 21px */
`;

const StyledMenuItem = styled(MenuItem)<{ $restricted?: boolean }>`
    &&& {
        display: flex;
        gap: 10px;
        color: ${props => props.$restricted ? "#999" : "inherit"};

        ${StyledSpan} {
            color: ${props => props.$restricted ? "#999" : "#222"};
        }

        &:hover {
            ${StyledSpan} {
                color: white;
            }
        }
    }
`;

export async function getPresentationResourcesInFolder(folderId: string): Promise<IWorkspaceResource[]> {
    return getWorkspaceResources({
        workspaceId: WorkspaceController.workspaceId,
        resourceTypes: [WorkspaceResourceType.PRESENTATION],
        resourceFilters: [{ field: "folderIds", operator: "eq", value: folderId }],
    });
}

export function isPresentationFolderNameTaken(
    name: string,
    folders: IWorkspaceResourceFolder[],
    {
        parentFolderId,
        folderTypes,
        excludeFolderId,
    }: {
        parentFolderId?: string | null;
        folderTypes?: WorkspaceResourceFolderType[];
        excludeFolderId?: string;
    }
) {
    return folders
        .filter(f => {
            if (!f.resourceTypes.includes(WorkspaceResourceType.PRESENTATION)) {
                return false;
            }
            if (excludeFolderId && f.id === excludeFolderId) {
                return false;
            }
            if (folderTypes && !folderTypes.includes(f.folderType)) {
                return false;
            }
            if (parentFolderId) {
                return f.parentFolderId === parentFolderId;
            }
            return !f.parentFolderId;
        })
        .some(f => f.name === name);
}

interface FolderMenuItemsProps {
    folder: IWorkspaceResourceFolder;
    resourceFolders: IWorkspaceResourceFolder[];
    onSetFilter: (filter: PresentationLibraryFilter) => void;
}

interface FolderMenuItemsWithPermissionsProps extends FolderMenuItemsProps {
    permissions: IWorkspacePermissionAccess;
}

export function FolderMenuItems({ folder, resourceFolders, onSetFilter, permissions }: FolderMenuItemsWithPermissionsProps) {
    const canManageFolder = permissions.write.object;
    const canDeleteFolder = permissions.owner;
    const isSharedWithOthers = WorkspaceController.isFolderSharedWithOthers(folder);
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );

    const handleEditSharing = () => {
        ShowDialog(ManageTeamFolderMembersDialog, {
            folderId: folder.id,
        });
    };

    const handleUpgradeSharing = () => {
        ShowDialog(ChangePlanDialog, {
            featuredPlan: WorkspacePlanTier.TEAM,
            analytics: { cta: "FolderSharing" },
        });
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: null,
                folderTypes: [WorkspaceResourceFolderType.SHARED],
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][Folder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message,
            });
        }
    };

    const handleCreateSubFolder = async () => {
        const siblingSubFolders = resourceFolders.filter(f => f.parentFolderId === folder.id);
        if (siblingSubFolders.length > 49) {
            ShowMessageDialog({
                title: t("This sub-folder cannot be added."),
                message: t("This folder already has the maximum of 50 sub-folders supported."),
                buttonText: t("Got it"),
                buttonOptions: { color: "blue", variant: "text" },
            });
            return;
        }

        let name = await ShowInputDialog({
            title: t("New Subfolder"),
            maxChars: 100,
            value: t("New Subfolder"),
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (!name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, { parentFolderId: folder.id })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            const createdFolder = await withProgressDialog({
                title: t("Creating subfolder..."),
                action: () => WorkspaceController.createWorkspaceResourceFolder({
                    parentFolderId: folder.id,
                    name,
                    resourceTypes: [WorkspaceResourceType.PRESENTATION],
                    folderType: WorkspaceResourceFolderType.SHARED,
                }),
            });

            // After creating the subfolder, set the filter to the new subfolder
            onSetFilter({
                type: PresentationLibraryFilterType.FOLDER,
                folderId: folder.id,
                subFolderId: createdFolder.id,
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][Folder] handleCreateSubFolder() Unable to create subfolder");

            ShowErrorDialog({
                title: t("Unable to create subfolder"),
                message: err.message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, {
            folder,
            resourceFolders,
            resourceTypes: [WorkspaceResourceType.PRESENTATION],
        });

        if (deleted) {
            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });
        }
    };

    const handleLeaveFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Leave this team folder?"),
            message: t("You may lose access to presentations in this folder. You can rejoin only if a team owner or librarian invites you."),
        })) {
            onSetFilter({ type: PresentationLibraryFilterType.ALL_PRESENTATIONS });

            try {
                await withProgressDialog({
                    title: t("Leaving team folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: { remove: [{ subjectId: app.user.id, subjectType: WorkspacePermissionSubjectType.USER }] },
                    }),
                });
            } catch (err) {
                logger.error(err, "[PresentationLibraryFolderMenu][Folder] handleLeaveFolder() Unable to leave team folder");

                ShowErrorDialog({
                    title: t("Unable to leave team folder"),
                    message: err.message,
                });
            }
        }
    };

    return (
        <>
            {!isAllTeamMembersFolder(folder) && (
                <StyledMenuItem
                    onClick={canUpgradeToManageSharedResources ? handleUpgradeSharing : handleEditSharing}
                    $restricted={canUpgradeToManageSharedResources}
                >
                    {canUpgradeToManageSharedResources ? (
                        <UpgradeIcon style={{ marginRight: 0 }} />
                    ) : (
                        <StyledIcon>person_add</StyledIcon>
                    )}
                    <StyledSpan>
                        {isSharedWithOthers
                            ? (permissions.write.permissions ? t("Manage Sharing...") : t("View Members..."))
                            : t("Share Folder...")}
                    </StyledSpan>
                </StyledMenuItem>
            )}
            {canManageFolder && (
                <StyledMenuItem onClick={handleRenameFolder}>
                    <StyledIcon>drive_file_rename_outline</StyledIcon>
                    <StyledSpan>{t("Rename Folder...")}</StyledSpan>
                </StyledMenuItem>
            )}
            {canManageFolder && (
                <StyledMenuItem divider onClick={handleCreateSubFolder}>
                    <StyledIcon>create_new_folder</StyledIcon>
                    <StyledSpan>{`${t("Create Subfolder")}...`}</StyledSpan>
                </StyledMenuItem>
            )}
            {canDeleteFolder && (
                <StyledMenuItem onClick={handleDeleteFolder}>
                    <StyledIcon>delete</StyledIcon>
                    <StyledSpan>{t("Delete Folder")}</StyledSpan>
                </StyledMenuItem>
            )}
            {!permissions.owner && (
                <StyledMenuItem onClick={handleLeaveFolder}>
                    <StyledIcon>logout</StyledIcon>
                    <StyledSpan>{t("Leave Folder")}</StyledSpan>
                </StyledMenuItem>
            )}
        </>
    );
}

export function SubFolderMenuItems({ folder, resourceFolders, onSetFilter, permissions }: FolderMenuItemsWithPermissionsProps) {
    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Subfolder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isPresentationFolderNameTaken(name, resourceFolders, {
                parentFolderId: folder.parentFolderId,
                excludeFolderId: folder.id,
            })) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            await withProgressDialog({
                title: t("Renaming subfolder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryFolderMenu][SubFolder] handleRenameFolder() Unable to rename subfolder");

            ShowErrorDialog({
                title: t("Unable to rename subfolder"),
                message: err.message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, {
            folder,
            resourceFolders,
            resourceTypes: [WorkspaceResourceType.PRESENTATION],
            // Only presentations move with write access - anything else the folder happens to hold
            // keeps the read-only default
            movePermissions: {
                [WorkspaceResourceType.PRESENTATION]: {
                    read: { object: true, permissions: true },
                    write: { object: true, permissions: true },
                },
            },
        });

        if (deleted) {
            onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.parentFolderId });
        }
    };

    if (permissions.write.object) {
        return (
            <>
                <StyledMenuItem onClick={handleRenameFolder}>
                    <StyledIcon>drive_file_rename_outline</StyledIcon>
                    <StyledSpan>{t("Rename Subfolder...")}</StyledSpan>
                </StyledMenuItem>
                <hr />
                <StyledMenuItem onClick={handleDeleteFolder}>
                    <StyledIcon>delete</StyledIcon>
                    <StyledSpan>{t("Delete Subfolder")}</StyledSpan>
                </StyledMenuItem>
            </>
        );
    }

    return null;
}

interface PresentationLibraryFolderHeaderMenuProps {
    filter: PresentationLibraryFilter;
    readOnly?: boolean;
    onSetFilter: (filter: PresentationLibraryFilter) => void;
}

function PresentationLibraryFolderHeaderMenuInner({
    filter,
    readOnly,
    onSetFilter,
    resourceFolders,
    workspace: { id: workspaceId },
}: PresentationLibraryFolderHeaderMenuProps & WorkspaceControllerState) {
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());

    const folderId = filter.subFolderId || filter.folderId;
    const folder = resourceFolders.find(f => f.id === folderId);
    const isInFolderFilter = filter.type === PresentationLibraryFilterType.FOLDER;
    const isSubFolder = !!filter.subFolderId;

    useEffect(() => {
        if (!isInFolderFilter || !folder || !filter.folderId) {
            return;
        }

        (async () => {
            try {
                const fetchedPermissions = await getUserPermissionsToResourceFolder({
                    workspaceId,
                    folderId: filter.folderId,
                });
                setPermissions(fetchedPermissions);
            } catch (err) {
                logger.error(err, "[PresentationLibraryFolderMenu] useEffect() Unable to fetch permissions");
            }
        })();
    }, [filter.folderId, isInFolderFilter, folder, workspaceId]);

    if (!folder || readOnly || app.isConstrained || !isInFolderFilter) {
        return null;
    }

    if (isSubFolder && !permissions.write.object) {
        return null;
    }

    const menuItemProps = {
        folder,
        resourceFolders,
        onSetFilter,
        permissions,
    };

    const menuItems = isSubFolder ? <SubFolderMenuItems {...menuItemProps} />
        : <FolderMenuItems {...menuItemProps} />;

    return (
        <Tooltip title={t("Manage folder")}>
            <HeaderActionIconMenu>
                {menuItems}
            </HeaderActionIconMenu>
        </Tooltip>
    );
}

export const PresentationLibraryFolderHeaderMenu = WorkspaceController.withInitializedState(
    PresentationLibraryFolderHeaderMenuInner
);
